import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { BOOKMAKERS } from '../../surebets/constants/surebetTypes';
import FilterPersistence from '../services/FilterPersistence';

/**
 * FilterContext - Context for managing filter state across the application
 * 
 * Features:
 * - Bookmaker selection state management
 * - LocalStorage persistence for filter preferences
 * - Integration with SurebetContext for data filtering
 * - Reset and select all functionality
 * - Validation and error handling
 */

// Default filter state
const DEFAULT_FILTER_STATE = {
  // Selected bookmakers (array of bookmaker IDs)
  selectedBookmakers: [],
  
  // Filter UI state
  isFilterPanelOpen: false,
  
  // Loading state for filter operations
  isApplyingFilters: false,
  
  // Error state
  filterError: null,
  
  // Actions
  selectBookmaker: () => {},
  deselectBookmaker: () => {},
  selectAllBookmakers: () => {},
  deselectAllBookmakers: () => {},
  toggleBookmaker: () => {},
  setSelectedBookmakers: () => {},
  resetFilters: () => {},
  applyFilters: () => {},
  toggleFilterPanel: () => {},
  clearFilterError: () => {},
  
  // Computed properties
  selectedBookmakerCount: 0,
  totalBookmakerCount: 0,
  hasActiveFilters: false,
  availableBookmakers: [],
};

// Context
const FilterContext = createContext(DEFAULT_FILTER_STATE);

/**
 * FilterProvider - Provider component for filter context
 * Manages bookmaker selection and filter state with persistence
 */
export const FilterProvider = ({ children }) => {
  // State
  const [selectedBookmakers, setSelectedBookmakers] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [filterError, setFilterError] = useState(null);

  /**
   * Get all available bookmakers (alphabetically by name to avoid any appearance of promotion)
   */
  const availableBookmakers = useMemo(() => {
    return Object.keys(BOOKMAKERS)
      .map(id => ({
        id,
        name: BOOKMAKERS[id].name,
        logo: BOOKMAKERS[id].logo,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  /**
   * Load filter preferences from localStorage on mount
   */
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        console.log('📂 FilterContext: Loading preferences using FilterPersistence');
        const result = await FilterPersistence.loadFilterPreferences();
        
        if (result.success) {
          setSelectedBookmakers(result.data.selectedBookmakers);
          console.log('✅ FilterContext: Loaded filter preferences:', result.data.selectedBookmakers);
        } else {
          console.warn('⚠️ FilterContext: Failed to load preferences:', result.error);
          // Fallback to all bookmakers
          const allBookmakerIds = Object.keys(BOOKMAKERS);
          setSelectedBookmakers(allBookmakerIds);
        }
      } catch (error) {
        console.error('❌ FilterContext: Error loading filter preferences:', error);
        // Fallback to all bookmakers
        const allBookmakerIds = Object.keys(BOOKMAKERS);
        setSelectedBookmakers(allBookmakerIds);
      }
    };

    loadPreferences();
  }, []);

  /**
   * Save filter preferences to localStorage whenever they change
   */
  useEffect(() => {
    if (selectedBookmakers.length > 0) {
      const savePreferences = async () => {
        try {
          const preferences = {
            selectedBookmakers,
            lastUpdated: new Date().toISOString(),
          };
          
          console.log('💾 FilterContext: Saving preferences using FilterPersistence');
          const result = await FilterPersistence.saveFilterPreferences(preferences);
          
          if (result.success) {
            console.log('✅ FilterContext: Successfully saved filter preferences');
          } else {
            console.error('❌ FilterContext: Failed to save preferences:', result.error);
            setFilterError('Failed to save filter preferences');
          }
        } catch (error) {
          console.error('❌ FilterContext: Error saving filter preferences:', error);
          setFilterError('Failed to save filter preferences');
        }
      };

      savePreferences();
    }
  }, [selectedBookmakers]);

  /**
   * Select a single bookmaker
   */
  const selectBookmaker = useCallback((bookmakerId) => {
    if (!BOOKMAKERS.hasOwnProperty(bookmakerId)) {
      console.warn('⚠️ FilterContext: Invalid bookmaker ID:', bookmakerId);
      return;
    }
    
    setSelectedBookmakers(prev => {
      if (prev.includes(bookmakerId)) {
        return prev; // Already selected
      }
      return [...prev, bookmakerId];
    });
  }, []);

  /**
   * Deselect a single bookmaker
   */
  const deselectBookmaker = useCallback((bookmakerId) => {
    setSelectedBookmakers(prev => prev.filter(id => id !== bookmakerId));
  }, []);

  /**
   * Toggle bookmaker selection
   */
  const toggleBookmaker = useCallback((bookmakerId) => {
    if (!BOOKMAKERS.hasOwnProperty(bookmakerId)) {
      console.warn('⚠️ FilterContext: Invalid bookmaker ID:', bookmakerId);
      return;
    }
    
    setSelectedBookmakers(prev => {
      if (prev.includes(bookmakerId)) {
        return prev.filter(id => id !== bookmakerId);
      }
      return [...prev, bookmakerId];
    });
  }, []);

  /**
   * Select all bookmakers
   */
  const selectAllBookmakers = useCallback(() => {
    const allBookmakerIds = Object.keys(BOOKMAKERS);
    setSelectedBookmakers(allBookmakerIds);
    console.log('✅ FilterContext: Selected all bookmakers');
  }, []);

  /**
   * Deselect all bookmakers
   */
  const deselectAllBookmakers = useCallback(() => {
    setSelectedBookmakers([]);
    console.log('✅ FilterContext: Deselected all bookmakers');
  }, []);

  /**
   * Reset filters to default (all selected)
   */
  const resetFilters = useCallback(() => {
    const allBookmakerIds = Object.keys(BOOKMAKERS);
    setSelectedBookmakers(allBookmakerIds);
    setFilterError(null);
    console.log('✅ FilterContext: Reset filters to default');
  }, []);

  /**
   * Apply filters (placeholder for integration with SurebetContext)
   * This will trigger a refresh of surebet data with new filters
   */
  const applyFilters = useCallback(async () => {
    try {
      setIsApplyingFilters(true);
      setFilterError(null);
      
      // Validate: at least one bookmaker must be selected
      if (selectedBookmakers.length === 0) {
        throw new Error('Please select at least one bookmaker');
      }
      
      console.log('✅ FilterContext: Applying filters:', selectedBookmakers);
      
      // Integration point: This will be connected to SurebetContext
      // to trigger data refresh with selected bookmakers
      // For now, we just log the action
      
      // Simulate async operation
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setIsApplyingFilters(false);
      
      return { success: true, selectedBookmakers };
    } catch (error) {
      console.error('❌ FilterContext: Error applying filters:', error);
      setFilterError(error.message);
      setIsApplyingFilters(false);
      
      return { success: false, error: error.message };
    }
  }, [selectedBookmakers]);

  /**
   * Toggle filter panel visibility
   */
  const toggleFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(prev => !prev);
  }, []);

  /**
   * Clear filter error
   */
  const clearFilterError = useCallback(() => {
    setFilterError(null);
  }, []);

  /**
   * Computed properties
   */
  const selectedBookmakerCount = selectedBookmakers.length;
  const totalBookmakerCount = Object.keys(BOOKMAKERS).length;
  const hasActiveFilters = selectedBookmakerCount < totalBookmakerCount;

  /**
   * Context value
   */
  const value = useMemo(() => ({
    // State
    selectedBookmakers,
    isFilterPanelOpen,
    isApplyingFilters,
    filterError,
    
    // Actions
    selectBookmaker,
    deselectBookmaker,
    selectAllBookmakers,
    deselectAllBookmakers,
    toggleBookmaker,
    setSelectedBookmakers,
    resetFilters,
    applyFilters,
    toggleFilterPanel,
    clearFilterError,
    
    // Computed properties
    selectedBookmakerCount,
    totalBookmakerCount,
    hasActiveFilters,
    availableBookmakers,
  }), [
    selectedBookmakers,
    isFilterPanelOpen,
    isApplyingFilters,
    filterError,
    selectBookmaker,
    deselectBookmaker,
    selectAllBookmakers,
    deselectAllBookmakers,
    toggleBookmaker,
    resetFilters,
    applyFilters,
    toggleFilterPanel,
    clearFilterError,
    selectedBookmakerCount,
    totalBookmakerCount,
    hasActiveFilters,
    availableBookmakers,
  ]);

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
};

FilterProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * useFilter - Custom hook to access filter context
 * Must be used within FilterProvider
 */
export const useFilter = () => {
  const context = useContext(FilterContext);
  
  if (!context) {
    throw new Error('useFilter must be used within FilterProvider');
  }
  
  return context;
};

export default FilterContext;

